import React, { useCallback, useEffect, useState } from 'react';
import { useApiAuth } from '../../hooks/useApiAuth';
import { PORTFOLIO_CATEGORIES } from '../../pages/Portfolio';

// ---------- Defaults (mirror the admin managers verbatim) ----------

const DEFAULT_HERO = {
  title: 'Thoughtfully Handmade, Beautifully Designed',
  subtitle: "Find meaningful, one-of-a-kind pieces perfect for gifting or adding a personal touch to your home.",
  cta_primary: { label: 'Explore Handmade Goods', href: '/portfolio' },
  cta_secondary: { label: 'Shop The Collection', href: '/services' },
  image_url: '',
};
const DEFAULT_HOME_FEATURES = [
  { title: 'Hand Crafted', description: 'Every piece is made by hand with care and attention to detail.' },
  { title: 'Collaborative Design', description: 'We work closely with you to bring your vision to life.' },
  { title: 'Functional Beauty', description: 'Our pieces are crafted to be as functional as they are beautiful — made to be used and enjoyed every day.' },
];
const DEFAULT_HOME_CLOSING = {
  title: "Let's work together to achieve your goals.",
  cta_label: 'Contact Us Today',
  cta_href: '/contact',
};
const DEFAULT_CONTACT = { intro: "Have a question or project in mind? We'd love to hear from you.", email: 'loganccurtis@gmail.com', phone: '(307) 409-2600', form_endpoint: '' };
const DEFAULT_SOCIALS = { linkedin: '', twitter: '', facebook: '', instagram: '' };
const DEFAULT_FOOTER = { tagline: 'Find meaningful, one-of-a-kind pieces perfect for gifting or adding a personal touch to your home.', copyright_name: 'Kalsies Creations' };
const DEFAULT_PROCESS = [
  { step: '01', title: 'Share', description: 'Tell us your ideas and vision.' },
  { step: '02', title: 'Design', description: 'We plan and confirm your custom piece.' },
  { step: '03', title: 'Craft', description: 'Handmade with care and quality materials.' },
  { step: '04', title: 'Enjoy', description: 'Receive and treasure your unique creation.' },
];
const DEFAULT_ABOUT = {
  heading: 'About Us',
  mission: 'Our mission is to create meaningful, handcrafted pieces that bring beauty, function, and a personal touch into everyday life. We aim to turn simple materials into lasting items people can cherish and share.',
  paragraphs: [],
  values: [],
  team: [],
};

// ---------- HERO ----------

export function HeroForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      setHero({
        ...DEFAULT_HERO,
        ...(data.hero || {}),
        cta_primary: { ...DEFAULT_HERO.cta_primary, ...((data.hero || {}).cta_primary || {}) },
        cta_secondary: { ...DEFAULT_HERO.cta_secondary, ...((data.hero || {}).cta_secondary || {}) },
      });
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const uploadHeroImage = async (file) => {
    setBusy(true); setStatus('Uploading…');
    try {
      const up = await authFetch(`/api/admin/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
      setHero((h) => ({ ...h, image_url: up.url }));
      setStatus('Image uploaded. Click Save.');
    } catch (e) { setStatus(`Upload error: ${e.message}`); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'hero', value: hero } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      <label>Big headline
        <input type="text" value={hero.title || ''} onChange={(e) => setHero({ ...hero, title: e.target.value })} />
      </label>
      <label>Sentence under the headline
        <textarea rows="2" value={hero.subtitle || ''} onChange={(e) => setHero({ ...hero, subtitle: e.target.value })} />
      </label>
      <label>First button — what it says
        <input type="text" value={hero.cta_primary?.label || ''} onChange={(e) => setHero({ ...hero, cta_primary: { ...(hero.cta_primary || {}), label: e.target.value } })} />
      </label>
      <label>First button — where it goes
        <input type="text" value={hero.cta_primary?.href || ''} onChange={(e) => setHero({ ...hero, cta_primary: { ...(hero.cta_primary || {}), href: e.target.value } })} />
      </label>
      <label>Second button — what it says
        <input type="text" value={hero.cta_secondary?.label || ''} onChange={(e) => setHero({ ...hero, cta_secondary: { ...(hero.cta_secondary || {}), label: e.target.value } })} />
      </label>
      <label>Second button — where it goes
        <input type="text" value={hero.cta_secondary?.href || ''} onChange={(e) => setHero({ ...hero, cta_secondary: { ...(hero.cta_secondary || {}), href: e.target.value } })} />
      </label>
      <label>Banner image address
        <input type="text" value={hero.image_url || ''} onChange={(e) => setHero({ ...hero, image_url: e.target.value })} placeholder="Leave empty to keep the current banner" />
      </label>
      <label>Or upload a new banner photo
        <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadHeroImage(f); }} />
      </label>
      {hero.image_url && <img src={hero.image_url} alt="banner preview" style={{ maxWidth: 240, borderRadius: 8 }} />}
      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save banner</button>
    </div>
  );
}

// ---------- HOME FEATURES ----------

export function HomeFeaturesForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [features, setFeatures] = useState(DEFAULT_HOME_FEATURES);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      setFeatures(Array.isArray(data.home_features) && data.home_features.length ? data.home_features : DEFAULT_HOME_FEATURES);
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const upd = (i, k, v) => { const next = features.slice(); next[i] = { ...next[i], [k]: v }; setFeatures(next); };

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'home_features', value: features } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      {features.map((f, i) => (
        <div key={i} className="admin-card">
          <label>Box heading
            <input type="text" value={f.title || ''} onChange={(e) => upd(i, 'title', e.target.value)} />
          </label>
          <label>Box description
            <textarea rows="2" value={f.description || ''} onChange={(e) => upd(i, 'description', e.target.value)} />
          </label>
          <button className="admin-btn" onClick={() => setFeatures(features.filter((_, j) => j !== i))}>Remove this box</button>
        </div>
      ))}
      <button className="admin-btn" onClick={() => setFeatures([...features, { title: '', description: '' }])}>+ Add another box</button>
      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save boxes</button>
    </div>
  );
}

// ---------- HOME CLOSING ----------

export function HomeClosingForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [closing, setClosing] = useState(DEFAULT_HOME_CLOSING);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      setClosing({ ...DEFAULT_HOME_CLOSING, ...(data.home_closing || {}) });
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'home_closing', value: closing } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      <label>Invitation line
        <input type="text" value={closing.title || ''} onChange={(e) => setClosing({ ...closing, title: e.target.value })} />
      </label>
      <label>Button text
        <input type="text" value={closing.cta_label || ''} onChange={(e) => setClosing({ ...closing, cta_label: e.target.value })} />
      </label>
      <label>Button link
        <input type="text" value={closing.cta_href || ''} onChange={(e) => setClosing({ ...closing, cta_href: e.target.value })} />
      </label>
      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save invitation</button>
    </div>
  );
}

// ---------- CONTACT ----------

export function ContactForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [contact, setContact] = useState(DEFAULT_CONTACT);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      setContact({ ...DEFAULT_CONTACT, ...(data.contact || {}) });
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'contact', value: contact } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      <label>Intro sentence on the Contact page
        <textarea rows="2" value={contact.intro || ''} onChange={(e) => setContact({ ...contact, intro: e.target.value })} />
      </label>
      <label>Email address
        <input type="email" value={contact.email || ''} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
      </label>
      <label>Phone number
        <input type="text" value={contact.phone || ''} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
      </label>
      <label>Contact form submissions go to (paste your Formspree URL — otherwise the form just shows a thank you)
        <input type="text" value={contact.form_endpoint || ''} onChange={(e) => setContact({ ...contact, form_endpoint: e.target.value })} />
      </label>
      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save contact info</button>
    </div>
  );
}

// ---------- SOCIALS ----------

export function SocialsForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [socials, setSocials] = useState(DEFAULT_SOCIALS);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      setSocials({ ...DEFAULT_SOCIALS, ...(data.socials || {}) });
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'socials', value: socials } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      {['linkedin', 'twitter', 'facebook', 'instagram'].map((k) => (
        <label key={k}>{k[0].toUpperCase() + k.slice(1)} address
          <input type="text" value={socials[k] || ''} onChange={(e) => setSocials({ ...socials, [k]: e.target.value })} placeholder={`https://www.${k}.com/…`} />
        </label>
      ))}
      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save social links</button>
    </div>
  );
}

// ---------- FOOTER ----------

export function FooterForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [footer, setFooter] = useState(DEFAULT_FOOTER);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      setFooter({ ...DEFAULT_FOOTER, ...(data.footer || {}) });
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'footer', value: footer } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      <label>Short description in the footer
        <textarea rows="2" value={footer.tagline || ''} onChange={(e) => setFooter({ ...footer, tagline: e.target.value })} />
      </label>
      <label>Business name for the copyright line
        <input type="text" value={footer.copyright_name || ''} onChange={(e) => setFooter({ ...footer, copyright_name: e.target.value })} />
      </label>
      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save footer</button>
    </div>
  );
}

// ---------- SERVICES PROCESS ----------

export function ServicesProcessForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [process, setProcess] = useState(DEFAULT_PROCESS);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const cfg = await authFetch('/api/admin/config');
      setProcess(Array.isArray(cfg.services_process) && cfg.services_process.length ? cfg.services_process : DEFAULT_PROCESS);
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'services_process', value: process } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      {process.map((p, i) => (
        <div key={i} className="admin-card">
          <label>Step number (like 01, 02, …)
            <input type="text" value={p.step || ''} onChange={(e) => { const n = process.slice(); n[i] = { ...n[i], step: e.target.value }; setProcess(n); }} />
          </label>
          <label>Title
            <input type="text" value={p.title || ''} onChange={(e) => { const n = process.slice(); n[i] = { ...n[i], title: e.target.value }; setProcess(n); }} />
          </label>
          <label>Description
            <textarea rows="2" value={p.description || ''} onChange={(e) => { const n = process.slice(); n[i] = { ...n[i], description: e.target.value }; setProcess(n); }} />
          </label>
          <button className="admin-btn" onClick={() => setProcess(process.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button className="admin-btn" onClick={() => setProcess([...process, { step: '', title: '', description: '' }])}>+ Add step</button>
      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save process</button>
    </div>
  );
}

// ---------- ABOUT ----------

export function AboutForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [about, setAbout] = useState(DEFAULT_ABOUT);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      const a = data.about || {};
      setAbout({
        heading: a.heading || 'About Us',
        mission: a.mission || '',
        paragraphs: Array.isArray(a.paragraphs) ? a.paragraphs : [],
        values: Array.isArray(a.values) ? a.values : [],
        team: Array.isArray(a.team) ? a.team : [],
      });
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const uploadFor = async (i, file) => {
    setBusy(true); setStatus('Uploading…');
    try {
      const up = await authFetch(`/api/admin/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
      const team = about.team.slice();
      team[i] = { ...team[i], image_url: up.url };
      setAbout({ ...about, team });
      setStatus('Photo uploaded. Click Save.');
    } catch (e) { setStatus(`Upload error: ${e.message}`); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'about', value: about } });
      onSaved();
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      <label>About page heading
        <input type="text" value={about.heading} onChange={(e) => setAbout({ ...about, heading: e.target.value })} />
      </label>
      <label>Mission statement
        <textarea rows="3" value={about.mission} onChange={(e) => setAbout({ ...about, mission: e.target.value })} />
      </label>

      <h3 style={{ margin: '12px 0 4px' }}>About page paragraphs</h3>
      {about.paragraphs.map((p, i) => (
        <div key={i} className="admin-card">
          <textarea rows="3" value={p} onChange={(e) => { const next = about.paragraphs.slice(); next[i] = e.target.value; setAbout({ ...about, paragraphs: next }); }} />
          <button className="admin-btn" onClick={() => setAbout({ ...about, paragraphs: about.paragraphs.filter((_, j) => j !== i) })}>Remove</button>
        </div>
      ))}
      <button className="admin-btn" onClick={() => setAbout({ ...about, paragraphs: [...about.paragraphs, ''] })}>+ Add paragraph</button>

      <h3 style={{ margin: '12px 0 4px' }}>Our Values boxes</h3>
      {about.values.map((v, i) => (
        <div key={i} className="admin-card">
          <label>Title
            <input type="text" value={v.title || ''} onChange={(e) => { const next = about.values.slice(); next[i] = { ...next[i], title: e.target.value }; setAbout({ ...about, values: next }); }} />
          </label>
          <label>Description
            <textarea rows="2" value={v.description || ''} onChange={(e) => { const next = about.values.slice(); next[i] = { ...next[i], description: e.target.value }; setAbout({ ...about, values: next }); }} />
          </label>
          <button className="admin-btn" onClick={() => setAbout({ ...about, values: about.values.filter((_, j) => j !== i) })}>Remove</button>
        </div>
      ))}
      <button className="admin-btn" onClick={() => setAbout({ ...about, values: [...about.values, { title: '', description: '' }] })}>+ Add value</button>

      <h3 style={{ margin: '12px 0 4px' }}>Meet Our Team</h3>
      {about.team.map((m, i) => (
        <div key={i} className="admin-card">
          <label>Name
            <input type="text" value={m.name || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], name: e.target.value }; setAbout({ ...about, team: t }); }} />
          </label>
          <label>Role
            <input type="text" value={m.role || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], role: e.target.value }; setAbout({ ...about, team: t }); }} />
          </label>
          <label>Bio
            <textarea rows="3" value={m.bio || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], bio: e.target.value }; setAbout({ ...about, team: t }); }} />
          </label>
          <label>Image URL
            <input type="text" value={m.image_url || ''} onChange={(e) => { const t = about.team.slice(); t[i] = { ...t[i], image_url: e.target.value }; setAbout({ ...about, team: t }); }} />
          </label>
          <label>Upload photo
            <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFor(i, f); }} />
          </label>
          {m.image_url && <img src={m.image_url} alt="preview" style={{ maxWidth: 120, borderRadius: '50%' }} />}
          <button className="admin-btn" onClick={() => setAbout({ ...about, team: about.team.filter((_, j) => j !== i) })}>Remove</button>
        </div>
      ))}
      <button className="admin-btn" onClick={() => setAbout({ ...about, team: [...about.team, { name: '', role: '', bio: '', image_url: '' }] })}>+ Add team member</button>

      <button className="admin-btn admin-btn-primary" disabled={busy} onClick={save}>Save About</button>
    </div>
  );
}

// ---------- SERVICES LIST ----------

export function ServicesForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', bullets: [] });

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/services');
      setItems((data.items || []).map((s) => ({ ...s, bullets: Array.isArray(s.bullets) ? s.bullets : [] })));
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const addService = async () => {
    if (!form.title.trim()) { setStatus('Title required.'); return; }
    setBusy(true); setStatus('Adding…');
    try {
      const sort_order = items.length;
      await authFetch('/api/admin/services', {
        method: 'POST',
        body: { title: form.title, description: form.description, bullets: form.bullets.filter(Boolean), sort_order },
      });
      setForm({ title: '', description: '', bullets: [] });
      await load();
      setStatus('Added.');
    } catch (e) { setStatus(`Add error: ${e.message}`); }
    setBusy(false);
  };

  const updItem = (i, patch) => { const next = items.slice(); next[i] = { ...next[i], ...patch }; setItems(next); };

  const saveItem = async (item) => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch(`/api/admin/services?id=${item.id}`, {
        method: 'PUT',
        body: { title: item.title, description: item.description, bullets: (item.bullets || []).filter(Boolean), sort_order: item.sort_order || 0 },
      });
      setStatus('Saved.');
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    setBusy(true);
    try {
      await authFetch(`/api/admin/services?id=${id}`, { method: 'DELETE' });
      await load();
      setStatus('Deleted.');
    } catch (e) { setStatus(`Delete error: ${e.message}`); }
    setBusy(false);
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[i]; const b = items[j];
    const next = items.slice();
    next[i] = { ...b, sort_order: i }; next[j] = { ...a, sort_order: j };
    setItems(next);
    setBusy(true);
    try {
      await authFetch('/api/admin/services', {
        method: 'PATCH',
        body: { items: [{ id: a.id, sort_order: j }, { id: b.id, sort_order: i }] },
      });
    } catch (e) { setStatus(`Reorder error: ${e.message}`); }
    setBusy(false);
  };

  const done = () => { onSaved(); };

  return (
    <div className="admin-form">
      <div className="admin-card">
        <strong>Add a new service</strong>
        <label>Title
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </label>
        <label>Description
          <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </label>
        <div>
          <strong>Bullets</strong>
          {form.bullets.map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input type="text" value={b} onChange={(e) => { const next = form.bullets.slice(); next[i] = e.target.value; setForm({ ...form, bullets: next }); }} style={{ flex: 1 }} />
              <button className="admin-btn" onClick={() => setForm({ ...form, bullets: form.bullets.filter((_, j) => j !== i) })}>×</button>
            </div>
          ))}
          <button className="admin-btn" style={{ marginTop: 6 }} onClick={() => setForm({ ...form, bullets: [...form.bullets, ''] })}>+ Bullet</button>
        </div>
        <button className="admin-btn admin-btn-primary" disabled={busy} onClick={addService}>Add service</button>
      </div>

      <h3 style={{ margin: '12px 0 4px' }}>Services ({items.length})</h3>
      {items.map((s, i) => (
        <div key={s.id} className="admin-card">
          <label>Title
            <input type="text" value={s.title || ''} onChange={(e) => updItem(i, { title: e.target.value })} />
          </label>
          <label>Description
            <textarea rows="2" value={s.description || ''} onChange={(e) => updItem(i, { description: e.target.value })} />
          </label>
          <div>
            <strong>Bullets</strong>
            {(s.bullets || []).map((b, k) => (
              <div key={k} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input type="text" value={b} onChange={(e) => { const nb = (s.bullets || []).slice(); nb[k] = e.target.value; updItem(i, { bullets: nb }); }} style={{ flex: 1 }} />
                <button className="admin-btn" onClick={() => updItem(i, { bullets: (s.bullets || []).filter((_, m) => m !== k) })}>×</button>
              </div>
            ))}
            <button className="admin-btn" style={{ marginTop: 6 }} onClick={() => updItem(i, { bullets: [...(s.bullets || []), ''] })}>+ Bullet</button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="admin-btn" disabled={busy || i === 0} onClick={() => move(i, -1)}>↑ Up</button>
            <button className="admin-btn" disabled={busy || i === items.length - 1} onClick={() => move(i, 1)}>↓ Down</button>
            <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveItem(s)}>Save</button>
            <button className="admin-btn admin-btn-danger" disabled={busy} onClick={() => removeItem(s.id)}>Delete</button>
          </div>
        </div>
      ))}
      <button className="admin-btn admin-btn-primary" onClick={done}>Done</button>
    </div>
  );
}

// ---------- TESTIMONIALS ----------

export function TestimonialsForm({ onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', body: '', rating: 5 });

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/testimonials');
      setItems(data.items || []);
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.body.trim()) { setStatus('Name and body required.'); return; }
    setBusy(true); setStatus('Adding…');
    try {
      await authFetch('/api/admin/testimonials', { method: 'POST', body: { ...form, rating: Number(form.rating) || 5, visible: true } });
      setForm({ name: '', role: '', body: '', rating: 5 });
      await load();
      setStatus('Added.');
    } catch (e) { setStatus(`Add error: ${e.message}`); }
    setBusy(false);
  };

  const toggle = async (item) => {
    setBusy(true);
    try {
      await authFetch(`/api/admin/testimonials?id=${item.id}`, { method: 'PATCH', body: { visible: !item.visible } });
      await load();
    } catch (e) { setStatus(`Update error: ${e.message}`); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    setBusy(true);
    try {
      await authFetch(`/api/admin/testimonials?id=${id}`, { method: 'DELETE' });
      await load();
    } catch (e) { setStatus(`Delete error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="admin-form">
      <div className="admin-card">
        <strong>Add a customer review</strong>
        <label>Name
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>Role (optional)
          <input type="text" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
        </label>
        <label>What they said
          <textarea rows="3" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </label>
        <label>Rating (1-5)
          <input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} />
        </label>
        <button className="admin-btn admin-btn-primary" disabled={busy} onClick={add}>Add testimonial</button>
      </div>

      <h3 style={{ margin: '12px 0 4px' }}>Customer reviews ({items.length})</h3>
      {items.length === 0 && <p>No testimonials yet.</p>}
      {items.map((t) => (
        <div key={t.id} className="admin-card">
          <div><strong>{t.name}</strong>{t.role ? ` — ${t.role}` : ''} <span className="admin-muted">({t.rating}/5)</span></div>
          <p>"{t.body}"</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="admin-btn" disabled={busy} onClick={() => toggle(t)}>{t.visible ? 'Hide' : 'Show'}</button>
            <button className="admin-btn admin-btn-danger" disabled={busy} onClick={() => remove(t.id)}>Delete</button>
          </div>
        </div>
      ))}
      <button className="admin-btn admin-btn-primary" onClick={onSaved}>Done</button>
    </div>
  );
}

// ---------- PORTFOLIO CATEGORY ----------

export function PortfolioCategoryForm({ categoryKey, onSaved, setStatus }) {
  const { authFetch } = useApiAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', file: null });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const cat = PORTFOLIO_CATEGORIES.find((c) => c.key === categoryKey) || { key: categoryKey, title: categoryKey };

  const load = useCallback(async () => {
    try {
      const p = await authFetch('/api/admin/portfolio');
      setItems((p.items || []).filter((i) => i.category === categoryKey));
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus, categoryKey]);
  useEffect(() => { load(); }, [load]);

  const addPhoto = async (e) => {
    e.preventDefault();
    if (!form.file) { setStatus('Pick an image first.'); return; }
    setBusy(true); setStatus('Uploading…');
    try {
      const up = await authFetch(`/api/admin/upload?filename=${encodeURIComponent(form.file.name)}`, { method: 'POST', body: form.file });
      const nextSort = (items.length + 1) * 10;
      await authFetch('/api/admin/portfolio', {
        method: 'POST',
        body: { category: categoryKey, title: form.title, description: form.description, image_url: up.url, sort_order: nextSort },
      });
      setForm({ title: '', description: '', file: null });
      setStatus('Photo added.');
      await load();
    } catch (e) { setStatus(`Add error: ${e.message}`); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    setBusy(true);
    try {
      await authFetch(`/api/admin/portfolio?id=${id}`, { method: 'DELETE' });
      setStatus('Deleted.');
      await load();
    } catch (e) { setStatus(`Delete error: ${e.message}`); }
    setBusy(false);
  };

  const startEdit = (it) => { setEditing(it.id); setEditForm({ title: it.title || '', description: it.description || '' }); };
  const cancelEdit = () => { setEditing(null); setEditForm({ title: '', description: '' }); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await authFetch(`/api/admin/portfolio?id=${editing}`, { method: 'PUT', body: editForm });
      setStatus('Updated.');
      cancelEdit();
      await load();
    } catch (e) { setStatus(`Update error: ${e.message}`); }
    setBusy(false);
  };

  const move = async (id, direction) => {
    const sorted = items.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
    const idx = sorted.findIndex((i) => i.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx]; const b = sorted[swapIdx];
    setBusy(true);
    try {
      await authFetch('/api/admin/portfolio', {
        method: 'PATCH',
        body: { items: [
          { id: a.id, sort_order: b.sort_order ?? swapIdx * 10 },
          { id: b.id, sort_order: a.sort_order ?? idx * 10 },
        ] },
      });
      await load();
    } catch (e) { setStatus(`Reorder error: ${e.message}`); }
    setBusy(false);
  };

  const sorted = items.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);

  return (
    <div className="admin-form">
      <div className="admin-card">
        <strong>Add a photo to {cat.title}</strong>
        <form onSubmit={addPhoto} className="admin-form">
          <label>Title (optional)
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label>Description (optional)
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <label>Image file
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
          </label>
          <button type="submit" disabled={busy} className="admin-btn admin-btn-primary">Add photo</button>
        </form>
      </div>

      <h3 style={{ margin: '12px 0 4px' }}>Photos ({sorted.length})</h3>
      {sorted.length === 0 && (
        <p className="admin-muted">
          No photos here yet. If you haven't hit <strong>Initialize DB</strong> in the Full dashboard, click that once — it imports the site's built-in photos.
        </p>
      )}
      {sorted.map((it, idx) => (
        <div key={it.id} className="admin-card">
          <img src={it.image_url} alt={it.title || cat.title} style={{ maxWidth: '100%', height: 160, objectFit: 'cover', borderRadius: 4 }} />
          {editing === it.id ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <input placeholder="Title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
              <input placeholder="Description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="admin-btn admin-btn-primary" onClick={saveEdit} disabled={busy}>Save</button>
                <button className="admin-btn" onClick={cancelEdit} disabled={busy}>Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              {it.title && <strong>{it.title}</strong>}
              {it.description && <div className="admin-muted">{it.description}</div>}
              {!it.title && !it.description && <div className="admin-muted">Untitled</div>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="admin-btn" disabled={busy || idx === 0} onClick={() => move(it.id, -1)}>↑</button>
            <button className="admin-btn" disabled={busy || idx === sorted.length - 1} onClick={() => move(it.id, 1)}>↓</button>
            {editing !== it.id && <button className="admin-btn" disabled={busy} onClick={() => startEdit(it)}>Edit</button>}
            <button className="admin-btn admin-btn-danger" disabled={busy} onClick={() => remove(it.id)}>Delete</button>
          </div>
        </div>
      ))}

      <button className="admin-btn admin-btn-primary" onClick={onSaved}>Done</button>
    </div>
  );
}
